// src/monitoring/components/ParkMap.jsx
//
// The portfolio on a map: one marker per park, coloured by status, sized by
// capacity. Same tile source and attribution as the site picker.

import React, { useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { MapContainer, TileLayer, CircleMarker, Tooltip as MapTooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { STATUS_HEX, fmtKw, fmtKwp, useLocale } from "./primitives";
import { useMonitoringText } from "../i18n";

const TILE_URL = process.env.REACT_APP_MAP_TILE_URL || "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
const TILE_ATTRIBUTION = process.env.REACT_APP_MAP_TILE_ATTRIBUTION || '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

export default function ParkMap({ parks, height = 320 }) {
  const t = useMonitoringText();
  const locale = useLocale();
  const points = useMemo(() => parks.filter((p) => Number.isFinite(Number(p.plant?.lat)) && Number.isFinite(Number(p.plant?.lon))), [parks]);
  const center = useMemo(() => {
    if (!points.length) return [38.5, 23.7];
    return [points.reduce((s, p) => s + Number(p.plant.lat), 0) / points.length, points.reduce((s, p) => s + Number(p.plant.lon), 0) / points.length];
  }, [points]);
  const maxCap = Math.max(1, ...points.map((p) => Number(p.capacity?.dc_kwp) || 0));
  if (!points.length) return null;
  return (
    <Box sx={{ height, borderRadius: 2, overflow: "hidden", border: "1px solid", borderColor: "divider", "& .leaflet-container": { height: "100%", width: "100%" } }}>
      <MapContainer center={center} zoom={points.length > 1 ? 6 : 10} scrollWheelZoom={false} style={{ height: "100%" }}>
        <TileLayer url={TILE_URL} attribution={TILE_ATTRIBUTION} />
        {points.map((p) => (
          <CircleMarker
            key={p.id}
            center={[Number(p.plant.lat), Number(p.plant.lon)]}
            radius={8 + 12 * Math.sqrt((Number(p.capacity?.dc_kwp) || 0) / maxCap)}
            pathOptions={{ color: STATUS_HEX[p.status] || "#9e9e9e", fillColor: STATUS_HEX[p.status] || "#9e9e9e", fillOpacity: 0.55, weight: 2 }}
          >
            <MapTooltip direction="top" offset={[0, -6]}>
              <Box component={RouterLink} to={`/monitoring/parks/${p.id}`} sx={{ textDecoration: "none", color: "inherit" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{p.name}</Typography>
                <Typography variant="caption" display="block">{fmtKwp(p.capacity?.dc_kwp, locale)} · {t(`status.${p.status}`)}</Typography>
                <Typography variant="caption" display="block">{t("portfolio.livePower")}: {fmtKw(p.live?.p_ac_kw, locale)}</Typography>
              </Box>
            </MapTooltip>
          </CircleMarker>
        ))}
      </MapContainer>
    </Box>
  );
}
